import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2, ShieldCheck, UserPlus, Trash2, Search, Award, ArrowLeft, Users, CalendarClock,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import {
  getMockTests,
  getMockTestPurchases,
  adminGrantMockTestAccess,
  revokeMockTestPurchase,
  type MockTestPurchase,
} from "@/api/lms/mock-tests";

export default function AdminMockTestPurchasesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedTestId, setSelectedTestId] = useState<string>("");
  const [grantUserId, setGrantUserId] = useState<string>("");
  const [grantAttempts, setGrantAttempts] = useState<number>(1);
  const [grantRef, setGrantRef] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: testsData, isLoading: testsLoading } = useQuery({
    queryKey: ["mockTests", "all"],
    queryFn: () => getMockTests({ page: 1, limit: 100 }),
  });

  const { data: purchases = [], isLoading: purchasesLoading } = useQuery({
    queryKey: ["mockTestPurchases", selectedTestId],
    queryFn: () => getMockTestPurchases(selectedTestId),
    enabled: !!selectedTestId,
  });

  const grantMutation = useMutation({
    mutationFn: () =>
      adminGrantMockTestAccess(selectedTestId, {
        userId: Number(grantUserId),
        attempts: grantAttempts,
        paymentRef: grantRef.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Access granted successfully!");
      queryClient.invalidateQueries({ queryKey: ["mockTestPurchases", selectedTestId] });
      setGrantUserId("");
      setGrantAttempts(1);
      setGrantRef("");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to grant access"),
  });

  const revokeMutation = useMutation({
    mutationFn: (purchaseId: string) => revokeMockTestPurchase(purchaseId),
    onSuccess: () => {
      toast.success("Purchase revoked");
      queryClient.invalidateQueries({ queryKey: ["mockTestPurchases", selectedTestId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to revoke"),
  });

  if (!user || user.role !== "admin") return null;

  const filteredPurchases = purchases.filter((p: MockTestPurchase) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const email = (p.user as any)?.email?.toLowerCase() ?? "";
    const name = (p.user as any)?.name?.toLowerCase() ?? "";
    return email.includes(q) || name.includes(q) || String(p.userId).includes(q);
  });

  const selectedTest = testsData?.items?.find(t => t.id === selectedTestId);

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="dash-page-header mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 shrink-0" asChild>
              <Link to="/mock-tests"><ArrowLeft className="size-4 mr-1" />Mock Tests</Link>
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title flex items-center gap-2 truncate">
                <ShieldCheck className="text-primary size-6 shrink-0" />
                Mock Test Purchase Management
              </h1>
              <p className="dash-page-subtitle truncate">
                Grant access, view purchases, and manage student attempts
              </p>
            </div>
          </div>
          <Button size="sm" asChild variant="outline" className="rounded-xl shrink-0">
            <Link to="/admin/mock-test-bundles">Manage Bundles</Link>
          </Button>
        </div>

        {/* Step 1: Select Test */}
        <KCard className="p-5 space-y-3">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            1. Select Mock Test
          </Label>
          {testsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="animate-spin size-4" /> Loading tests…
            </div>
          ) : (
            <select
              className="w-full h-9 px-3 rounded-xl border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
              value={selectedTestId}
              onChange={e => setSelectedTestId(e.target.value)}
            >
              <option value="">— Choose a mock test —</option>
              {testsData?.items?.map(t => (
                <option key={t.id} value={t.id}>
                  {t.title || t.level} {t.isPublished ? "" : "(Draft)"} — {Number(t.price ?? 0) === 0 ? "Free" : `${t.currency ?? "BDT"} ${t.price}`}
                </option>
              ))}
            </select>
          )}

          {selectedTest && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
              <span className="font-mono bg-muted/40 px-2 py-0.5 rounded">
                Price: {Number(selectedTest.price ?? 0) === 0 ? "Free" : `${selectedTest.currency ?? "BDT"} ${selectedTest.price}`}
              </span>
              <span className="font-mono bg-muted/40 px-2 py-0.5 rounded">
                Attempts/purchase: {selectedTest.attemptLimit ?? 1}
              </span>
              <span className="font-mono bg-muted/40 px-2 py-0.5 rounded">
                Access: {selectedTest.accessDays ? `${selectedTest.accessDays} days` : "Lifetime"}
              </span>
            </div>
          )}
        </KCard>

        {selectedTestId && (
          <>
            {/* Step 2: Grant Access */}
            <KCard className="p-5 space-y-4">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                2. Grant Access to Student
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="space-y-1.5 sm:col-span-1">
                  <Label className="text-xs font-semibold">Student User ID</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 42"
                    className="h-8 text-sm rounded-xl"
                    value={grantUserId}
                    onChange={e => setGrantUserId(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Attempts to Grant</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    className="h-8 text-sm rounded-xl"
                    value={grantAttempts}
                    onChange={e => setGrantAttempts(Math.max(1, Number(e.target.value) || 1))}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-1">
                  <Label className="text-xs font-semibold">Payment Ref (optional)</Label>
                  <Input
                    placeholder="e.g. TXN123"
                    className="h-8 text-sm rounded-xl"
                    value={grantRef}
                    onChange={e => setGrantRef(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    className="w-full h-8 text-xs rounded-xl font-bold"
                    disabled={!grantUserId || grantMutation.isPending}
                    onClick={() => grantMutation.mutate()}
                  >
                    {grantMutation.isPending
                      ? <Loader2 className="animate-spin size-3.5 mr-1" />
                      : <UserPlus className="size-3.5 mr-1" />
                    }
                    Grant Access
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                If student already has a purchase, their remaining attempts will be topped up by the amount specified.
              </p>
            </KCard>

            {/* Step 3: View Purchases */}
            <KCard className="p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  3. Current Purchases ({filteredPurchases.length})
                </Label>
                <div className="relative w-56">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email…"
                    className="pl-8 h-7 text-xs rounded-xl"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {purchasesLoading ? (
                <div className="dash-empty-state flex flex-row items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="animate-spin size-4" /> Loading purchases…
                </div>
              ) : filteredPurchases.length === 0 ? (
                <div className="dash-empty-state">
                  <Users className="size-10 mx-auto mb-4 opacity-30" />
                  <p className="text-sm">No purchases found for this test</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {filteredPurchases.map((purchase: MockTestPurchase) => {
                    const isExpired = purchase.expiresAt ? new Date(purchase.expiresAt) < new Date() : false;
                    const hasAttempts = purchase.remainingAttempts > 0;

                    return (
                      <div
                        key={purchase.id}
                        className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                            isExpired ? "bg-rose-100 text-rose-700" :
                            hasAttempts ? "bg-emerald-100 text-emerald-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            {String(purchase.userId).slice(-2)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">
                              {(purchase.user as any)?.name || (purchase.user as any)?.email || `User #${purchase.userId}`}
                            </div>
                            <div className="text-[10px] text-muted-foreground flex flex-wrap items-center gap-2">
                              <span className="font-mono">ID: {purchase.userId}</span>
                              <span>·</span>
                              <span className="font-mono">
                                Paid: {purchase.amountPaid > 0 ? `${purchase.currency} ${purchase.amountPaid}` : "Free"}
                              </span>
                              <span>·</span>
                              <span className="capitalize font-mono">{purchase.paymentMethod ?? "—"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-center">
                            <div className={`text-sm font-bold font-mono ${hasAttempts && !isExpired ? "text-emerald-600" : "text-rose-600"}`}>
                              {purchase.remainingAttempts}/{purchase.purchasedAttempts}
                            </div>
                            <div className="text-[9px] text-muted-foreground">Attempts</div>
                          </div>

                          {purchase.expiresAt ? (
                            <div className="text-center">
                              <div className={`text-xs font-mono ${isExpired ? "text-rose-600 font-bold" : "text-muted-foreground"}`}>
                                <CalendarClock className="size-3 inline mr-0.5" />
                                {new Date(purchase.expiresAt).toLocaleDateString()}
                              </div>
                              <div className="text-[9px] text-muted-foreground">{isExpired ? "Expired" : "Expires"}</div>
                            </div>
                          ) : (
                            <div className="text-center">
                              <Award className="size-4 text-emerald-500 mx-auto" />
                              <div className="text-[9px] text-muted-foreground">Lifetime</div>
                            </div>
                          )}

                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                            disabled={revokeMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Revoke access for user #${purchase.userId}?`)) {
                                revokeMutation.mutate(purchase.id);
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </KCard>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
